import React, { useCallback, useEffect, useState, useRef } from 'react';
import { makeStyles } from 'tss-react/mui';
import {
  Box,
  Card,
  CardContent,
  CardHeader,
  Table,
  TableBody,
  TableFooter,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  Typography,
  Skeleton,
  Button,
  useTheme,
  useMediaQuery
} from '@mui/material';
import Paper from '@mui/material/Paper';
import useSelectedGroup from 'src/hooks/useSelectedGroup';
import { useTranslation } from 'react-i18next';
import { Link as RouterLink } from 'react-router-dom';
import useAuth from 'src/hooks/useAuth';
import GetSubAccountAlerts from 'src/api/GetSubAccountAlerts';
import { ArrowUpRight, Settings, ChevronsUp as ScrollArrowsUp, ChevronsDown as ScrollArrowsDown } from 'lucide-react';
import TablePaginationComp from 'src/components/TablePaginationComp';
import CreateAlertButton from 'src/components/CreateAlertButton.js'
import { TableSortControls } from 'src/components/TableSortControls';
import { applySorting, getComparator } from 'src/utils/filterAndSortData';

const useStyles = makeStyles()((theme) => ({
    table: {
      minWidth: 650
    },
    metalTableHeaderRow: {
      height: '40px',
      backgroundColor: 'var(--color-surface-raised)'
    },
    metalTableDataRow: {
      height: '50px',
      backgroundColor: 'var(--color-surface)'
    },
    alertsBtn: {
      color: '#e6e5e8 !important',
      fontSize: '16px',
      textTransform: 'capitalize',
      borderRadius: "var(--radius-md, 8px)",
      border: "1px solid var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-border, #364152)",
      background: "var(--Component-colors-Components-Buttons-Secondary-color-button-secondary-color-bg, #121926)",
      boxShadow: "0px 1px 2px 0px rgba(16, 24, 40, 0.05)",
      whiteSpace: 'nowrap',
      '&:hover' : {
        textDecoration: "none !important"
      },
    },
    cardViewButton: {
      fontSize: '14px', 
      textTransform: 'capitalize',
      '&:hover' : {
        backgroundColor: "transparent"
      },
    },
    scrollButtonsContainer: {
      display: 'flex',
      flexDirection: 'column'
    },
    hideScrollButton: {
      display: 'none'
    },
    topScrollButton: {
      display: 'flex',
      alignSelf: 'center',
      position: 'absolute',
      top: '72px',
      height: '50px',
      width: '50px',
      backgroundColor: 'var(--color-accent)',
      outline: 'none',
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1001
    },
    bottomScrollButton: {
      position: 'absolute',
      display: 'flex',
      alignSelf: 'center',
      bottom: '12px',
      height: '50px',
      width: '50px',
      backgroundColor: 'var(--color-accent)',
      outline: 'none',
      border: '1px solid var(--color-border)',
      borderRadius: '8px',
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
      zIndex: 1001
    }
}));

const AlertsTable = (props) => {
  const { coin, update, setNoData, loading, setLoading } = props;
  const { selectedGroup } = useSelectedGroup();
  const { t } = useTranslation();
  const { classes } = useStyles();
  const [page, setPage] = React.useState(0);
  const [rowsPerPage, setRowsPerPage] = React.useState(10);
  const [rows, setRows] = useState([]);
  const [error, setError] = useState('');
  const [subAccountOrder, setSubAccountOrder] = useState('asc');
  const [usersOrderMobile, setUsersOrderMobile] = useState('asc')
  const [usersOrderByMobile, setUsersOrderByMobile] = useState('subAccountName')
  const emptyRows =
    rowsPerPage -
    Math.min(rowsPerPage, rows.length - page * rowsPerPage);
  const { getAccessToken, loggedInUser } = useAuth();
  const theme = useTheme();
  const mediumDevice = useMediaQuery(theme.breakpoints.down('md'));
  const smallerDevice = useMediaQuery(theme.breakpoints.down(450));

  const topElementRef = useRef(null);
  const lastElementRef = useRef(null);

  const handleScrollTop = e => {
    topElementRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const handleScrollBottom = e => {
    lastElementRef.current.scrollIntoView({ behavior: 'smooth' });
  };

  const useVisibility = (button, ref) => {
    const offset = 500;
    const [isVisible, setIsVisible] = useState(true || !loading);

    const onScroll = () => {
      if (!ref || !ref?.current) {
        setIsVisible(false);
        return;
      }
      const top = ref.current.getBoundingClientRect().top;
      setIsVisible(
        (button === 'top' ? top + offset >= 0 : top < offset) &&
          top - offset <= window.innerHeight
      );
    };

    useEffect(() => {
      document.addEventListener('scroll', _.throttle(onScroll, 1000), true);
      return () =>
        document.removeEventListener(
          'scroll',
          _.throttle(onScroll, 1000),
          true
        );
    });

    return isVisible;
  };

  let topIsVisible = useVisibility('top', mediumDevice ? topElementRef : null);
  let bottomIsVisible = useVisibility(
    'bottom',
    mediumDevice ? lastElementRef : null
  );

  const handleSubAccountColumnSort = () => {
    const isAsc = subAccountOrder === 'asc';
    setSubAccountOrder(isAsc ? 'desc' : 'asc');
  };

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = event => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  const handleMobileUsersCardsSort = (tableColumn, columnOrder) => {
    setUsersOrderMobile(columnOrder)
    setUsersOrderByMobile(tableColumn)
  }

  const renderTableSkeleton = () => {
    return (
      <TableBody>
        <TableRow className={classes.metalTableDataRow}>
          <TableCell align="left" aligncomponent="th" scope="row">
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          {loggedInUser?.groups?.length > 1 && (
            <TableCell align={'left'}>
              <Skeleton variant="rounded" height={20} width={100} />
            </TableCell>
          )}
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
          <TableCell align={'left'}>
            <Skeleton variant="rounded" height={20} width={100} />
          </TableCell>
        </TableRow>
      </TableBody>
    );
  };

  function Row(props) {
    const row = props.row;
    return (
      <React.Fragment>
        <TableRow className={classes.metalTableDataRow}>
          <TableCell
            style={{
              overflow: 'hidden',
              textOverflow: 'ellipsis'
            }}
            align="left"
            aligncomponent="th"
            scope="row"
          >
            {row.subAccountName}
          </TableCell>
          {loggedInUser?.groups?.length > 1 && (
            <TableCell
              style={{
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                fontSize: '16px',
                whiteSpace: 'nowrap'
              }}
              align={'left'}
            >
              {row.groupName}
            </TableCell>
          )}
          <TableCell style={{ fontSize: '16px', whiteSpace: 'nowrap' }} align={'left'}>
            {Object.keys(row.alertType)?.map((key, index) => {
              return (row.alertType[key] ? <div
              key={index}
              className="m-chip"
              data-color-status={ key === 'hashrate' ? 'info' : 'highlight'}
              style={{ textTransform: 'capitalize', marginRight: 5 }}
            >
              {t(`${key}`)}
              </div> : null)
            })}
          </TableCell>
          <TableCell
            style={{ fontSize: '16px', color: 'var(--color-text-accent)', whiteSpace: 'nowrap' }}
            align={'left'}
          >
              {Object.keys(row.alertInfo)?.map(( key, index) => {
              return (row.alertInfo[key] !== null ? <div
                key={index}
                className="m-chip"
                data-color-status={ key === 'hashrate' ? 'info' : 'highlight'}
                style={{ textTransform: 'capitalize', marginRight: 5 }}
            >
              {row.alertInfo[key].toLocaleString()} {(key === 'workers' &&  (row.alertInfo[key] === 1)) ? 'Worker'  : (key === 'workers' &&  (row.alertInfo[key] !== 1)) ? t(`${key}`) : ''}
              </div> : null)
            })}
          </TableCell>
          <TableCell
            style={{ fontSize: '16px', color: 'var(--color-text-warning)', whiteSpace: 'nowrap' }}
            align={'left'}
          >
            { row.hashrateThreshold !== null  && <div
              className="m-chip"
              data-color-status="info"
              style={{ textTransform: 'capitalize', marginRight: 5, display: 'inline-flex' }}
            >
              {row.hashrateThreshold}
            </div>}
            { row.activeWorkersThreshold > 0 && <div
              className="m-chip"
              data-color-status="highlight"
              style={{ textTransform: 'capitalize', marginRight: 5, display: 'inline-flex' }}
            >
              {row.activeWorkersThreshold.toLocaleString()} {row.activeWorkersThreshold === 1 ? 'Worker' : 'Workers'}
            </div> }
          </TableCell>
          <TableCell
            align={'center'}
          >
              <Button
                style={{ fontSize: '14px', textTransform: 'capitalize' }}
                color="inherit"
                component={RouterLink}
                to={{
                  pathname: `/app/sub-accounts/${row.subAccountName}/dashboard`,
                }}
                state= {{ root: t('subAccounts'), group: row.groupName}}
                startIcon={<ArrowUpRight size={20} />}
              >
                View
            </Button>
          </TableCell>
        </TableRow>
      </React.Fragment>
    );
  }

  const fetchData = useCallback(async () => {
      const {tokenAvailable, accessToken} = await getAccessToken()
      setLoading(true);
      
      if (!tokenAvailable) {
        return;
      }
      const {success, data} = await GetSubAccountAlerts(accessToken, coin, subAccountOrder === 'asc' ? 'ascSubAccount' : 'descSubAccount', selectedGroup, true);
      if (success){
        setRows(data)
        if(data.length) {
          setNoData(false)
        }
      }
      else{
        setRows([])
        setError(data)
      }   
      setLoading(false);
      
    },[getAccessToken, coin, selectedGroup, subAccountOrder]);

  useEffect(() => {
    setPage(0);
  }, [selectedGroup]);

  useEffect(() => {
      fetchData()
  }, [fetchData, update])

  const renderCards = () => {     
    return (
      <>
        {!!rows.length &&
          applySorting(rows, getComparator(usersOrderMobile, usersOrderByMobile)).map(
            (card, index) => {
              return (
                <Card ref={rows.length === index + 1 ? lastElementRef : null} key={card.alertId} variant='outlined' sx={{ marginTop: "10px", backgroundColor: "var(--neutral-2)", borderColor: "var(--color-border)"}}>
                  <CardHeader
                    sx={{paddingBottom: "0"}}
                    title={card.subAccountName}
                    subheader={card.groupName}
                    action={
                      <Button
                        className={classes.cardViewButton}
                        color="inherit"
                        component={RouterLink}
                        to={{
                          pathname: `/app/sub-accounts/${card.subAccountName}/dashboard`,
                        }}
                        state= {{ root: t('subAccounts'), group: card.groupName }}
                      >
                        <ArrowUpRight size={20} />
                    </Button>
                    }
                  />
                  <CardContent>
                    <Table>
                      <TableBody>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0, width: "50%"}}>
                            <Typography variant='h6'>{t('alertType')}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            {Object.keys(card.alertType)?.filter(h => h === "hashrate").map((key, index) => {
                              return (card.alertType[key] ? <div
                                key={index}
                                className="m-chip"
                                data-color-status={'info'}
                                style={{ textTransform: 'capitalize', marginRight: 5 }}
                              >
                              {t(`${key}`)}
                              </div> : null)
                            })}
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            {Object.keys(card.alertType)?.filter(h => h === 'workers').map((key, index) => {
                              return (card.alertType[key] ? <div
                              key={index}
                              className="m-chip"
                              data-color-status={'highlight'}
                              style={{ textTransform: 'capitalize', marginRight: 5 }}
                            >
                              {t(`${key}`)}
                              </div> : null)
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                            <Typography variant='h6'>{t('currentValue')}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            {Object.keys(card.alertInfo)?.filter(h => h === 'hashrate').map(( key, index) => {
                              return (card.alertInfo[key] !== null ? <div
                                key={index}
                                className="m-chip"
                                data-color-status={'info'}
                                style={{ textTransform: 'capitalize', marginRight: 5 }}
                            >

                              {card.alertInfo[key]}
                              </div> : null)
                            })}
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            {Object.keys(card.alertInfo)?.filter(h => h === 'workers').map(( key, index) => {
                              return (card.alertInfo[key] !== null ? <div
                                key={index}
                                className="m-chip"
                                data-color-status={'highlight'}
                                style={{ textTransform: 'capitalize', marginRight: 5 }}
                            >
                              {`${card.alertInfo[key].toLocaleString()} ${smallerDevice ? "" : card.alertInfo[key] === 1 ? "Worker" : "Workers" }`}
                              </div> : null)
                            })}
                          </TableCell>
                        </TableRow>
                        <TableRow>
                          <TableCell align="left" sx={{paddingLeft: 0, paddingRight: 0}}>
                            <Typography variant='h6'>{t('threshold')}</Typography>
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            { card.hashrateThreshold !== null  && <div
                              className="m-chip"
                              data-color-status="info"
                              style={{ textTransform: 'capitalize', marginRight: 5, display: 'inline-flex' }}
                            >
                              {card.hashrateThreshold}
                            </div>}
                          </TableCell>
                          <TableCell align="right" sx={{paddingLeft: 0, paddingRight: 0}}>
                            { card.activeWorkersThreshold > 0 && <div
                              className="m-chip"
                              data-color-status="highlight"
                              style={{ textTransform: 'capitalize', marginRight: 5, display: 'inline-flex' }}
                            >
                              {`${card.activeWorkersThreshold.toLocaleString()} ${smallerDevice ? "" : card.activeWorkersThreshold === 1 ? "Worker" : "Workers" }`}
                            </div> }
                          </TableCell>
                        </TableRow>
                      </TableBody>
                    </Table>
                  </CardContent>
                </Card>
              );
            }
          )}
      </>
    );
  } 


  return (
    mediumDevice 
      ? loading 
        ? <Card
            variant="outlined"
            sx={{
              backgroundColor: 'var(--neutral-2)',
              borderColor: 'var(--color-border)'
            }}
          >
            <Box
                sx={{
                  display: 'flex',
                  alignItems: "flex-start",
                  justifyContent: 'space-between',
                  marginBottom: '20px'
                }}
              >
                <CardHeader
                  subheader={
                    <Skeleton
                      height={24}
                      width={150}
                      variant="rounded"
                      sx={{
                        marginTop: "10px",
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                  title={
                    <Skeleton
                      height={24}
                      width={100}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
                <CardHeader
                  style={{ width: '100%' }}
                  align="right"
                  title={
                    <Skeleton
                      height={24}
                      width={'20%'}
                      variant="rounded"
                      sx={{
                        backgroundColor: 'var(--neutral-7)',
                        border: '1px solid var(--color-border)'
                      }}
                    />
                  }
                />
              </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardHeader
                style={{
                  marginLeft: 16,
                  paddingLeft: 0,
                  borderBottom: '1px solid var(--color-border)'
                }}
                title={
                  <Skeleton
                    height={24}
                    width={100}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
              <CardHeader
                style={{
                  marginRight: 16,
                  paddingRight: 0,
                  borderBottom: '1px solid var(--color-border)',
                  width: '100%'
                }}
                align="right"
                title={
                  <Skeleton
                    height={24}
                    width={'30%'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardHeader
                style={{
                  marginLeft: 16,
                  paddingLeft: 0,
                  borderBottom: '1px solid var(--color-border)'
                }}
                title={
                  <Skeleton
                    height={24}
                    width={100}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
              <CardHeader
                style={{
                  marginRight: 16,
                  paddingRight: 0,
                  borderBottom: '1px solid var(--color-border)',
                  width: '100%'
                }}
                align="right"
                title={
                  <Skeleton
                    height={24}
                    width={'30%'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
              <CardHeader
                style={{
                  marginLeft: 16,
                  paddingLeft: 0,
                }}
                title={
                  <Skeleton
                    height={24}
                    width={100}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
              <CardHeader
                style={{
                  marginRight: 16,
                  paddingRight: 0,
                  width: '100%'
                }}
                align="right"
                title={
                  <Skeleton
                    height={24}
                    width={'30%'}
                    variant="rounded"
                    sx={{
                      backgroundColor: 'var(--neutral-7)',
                      border: '1px solid var(--color-border)'
                    }}
                  />
                }
              />
            </Box>
          </Card>
        : rows.length 
          ? <Card 
              ref={topElementRef}
              style={{
                backgroundColor: 'var(--neutral-3)',
                padding: '0 var(--space-m) var(--space-m) var(--space-m)'
              }}>
              <Box sx={{marginTop: "var(--space-l)"}}>
                <TableSortControls
                  selectorValues={{     
                    clear: t("clear"),
                    subAccountName: t("subAccount")
                  }}
                  onChange={handleMobileUsersCardsSort}
                  defaultSort={'subAccountName'}
                  defaultOrder={'asc'}
                />
              </Box>
              <Box paddingX={2} pt={2}>
                <Box
                  display="flex"
                  justifyContent={'flex-end'}
                  alignItems="center"
                >
                  <Typography align="right">
                    Total Items: {rows.length}
                  </Typography>
                </Box>
              </Box>
              <div className={classes.scrollButtonsContainer}>
                <button
                  onClick={handleScrollTop}
                  className={
                    topIsVisible
                      ? classes.hideScrollButton
                      : classes.topScrollButton
                  }
                >
                  <ScrollArrowsUp color={'white'} />
                </button>
                <button
                  onClick={handleScrollBottom}
                  className={
                    bottomIsVisible
                      ? classes.hideScrollButton
                      : classes.bottomScrollButton
                  }
                >
                  <ScrollArrowsDown color={'white'} />
                </button>
                {
                  renderCards()
                }
              </div>
            </Card>
          : <Box
              display="flex"
              flexDirection="column"
              justifyItems="center"
              alignItems='center'
              style={{ background: 'var(--color-background)'}}
            >
              <div style={{ position: "relative"}}>
                <p style={{position: "absolute", left: 180, top: 280, fontFamily: 'var(--font-family)', fontSize: 16, fontWeight: 600, color: "#e6e5e8"}}>All clear, captain</p>
                <p style={{position: "absolute", left: 140, top: 310, fontFamily: 'var(--font-family)', fontSize: 14, fontWeight: 400, color: '#9AA4B2'}}>No active alerts at the moment.</p>
                <div style={{position: "absolute", width: 48, height: 48, left: 217, top: 217, border: '1px solid #364152', borderRadius: 'var(--radius-l)'}}></div>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{position: "absolute", left: 230, top: 230}}>
                  <path d="M20 6L9 17L4 12" stroke="#CDD5DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {mediumDevice ? <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                  <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                </svg> : 
                <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                  <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                  <circle cx="240" cy="240" r="207.5" stroke="#202939" opacity={0.2} />
                  <circle cx="240" cy="240" r="239.5" stroke="#202939" opacity={0.1} />
                </svg>}
              </div>
              {
                !rows.length && <div style={{position: "relative", top: "-80px"}}>
                  <Button
                    className={classes.alertsBtn}
                    component={RouterLink}
                    variant='outlined'
                    to='/app/alerts/manage'
                    startIcon={<Settings />}
                  >
                      {t('manageAlerts')}
                  </Button> 
                  <CreateAlertButton coin={coin}/>
                </div>
              }
            </Box>

      : <Card
          style={{
            backgroundColor: 'var(--uishell-color-surface)',
            boxShadow: 'none',
            marginBottom: 20
          }}
        >
          {(loading || rows.length > 0) || (!loading && error) ? <TableContainer
            component={Paper}
            style={{
              backgroundColor: 'var(--uishell-color-surface)',
              borderTopRightRadius: 0,
              borderTopLeftRadius: 0,
              borderTop: '2px solid var(--color-border)',
              position: 'relative',
              maxHeight: 'calc(var(--space-xl) * 20)',
              scrollbarWidth: loading ? 'none' : 'auto',
            }}
          >
            <Table className={classes.table} size="small">
              <TableHead>
                <TableRow
                  className={classes.metalTableHeaderRow}
                  style={{ whiteSpace: 'nowrap' }}
                >
                  <TableCell
                    align="left"
                  >
                    <TableSortLabel active={true} direction={subAccountOrder} onClick={() => handleSubAccountColumnSort()}>
                      {t('subAccount')}
                    </TableSortLabel>
                  </TableCell>
    
                  {loggedInUser?.groups?.length > 1 && (
                    <TableCell
                      align={'left'}
                    >
                        {t('groupName')}
                    </TableCell>
                  )}
    
                  <TableCell
                    align={'left'}
                  >
                    {t('alertType')}
                  </TableCell>
    
                  <TableCell
                    align={'left'}
                  >
                      {t('currentValue')}
                  </TableCell>
    
                  <TableCell
                    align={'left'}
                  >
                      Threshold
                  </TableCell>
                  <TableCell align={'center'}>
                    <></>
                  </TableCell>
                </TableRow>
              </TableHead>
    
              {rows.length > 0 && !loading && !error ? (
                <TableBody>
                  {(rowsPerPage > 0
                    ? rows.slice(
                        page * rowsPerPage,
                        page * rowsPerPage + rowsPerPage
                      )
                    : rows
                  ).map(row => (
                    <Row key={row.uniqueId} row={row} />
                  ))}
                  {emptyRows > 0 && (
                    <TableRow style={{ height: emptyRows }}>
                      <TableCell colSpan={6} />
                    </TableRow>
                  )}
                </TableBody>
              ) : !loading && error ?
                <TableBody>
                  <TableRow style={{ height: emptyRows * 5 }}>
                    <TableCell colSpan={6} align="center">
                      {' '}
                      {error}
                    </TableCell>
                </TableRow>
              </TableBody>
              
              : (
                renderTableSkeleton()
              )}
    
              <TableFooter style={{ position: 'sticky', bottom: -1}}>
                <TableRow>
                  <TablePaginationComp
                    filteredRows={rows}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    handleChangePage={handleChangePage}
                    handleChangeRowsPerPage={handleChangeRowsPerPage}
                    disablePagination={loading}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer> :
            <Box
              display="flex"
              flexDirection="column"
              justifyItems="center"
              alignItems='center'
              style={{ background: 'var(--color-background)'}}
            >
              <div style={{ position: "relative"}}>
                <p style={{position: "absolute", left: 180, top: 280, fontFamily: 'var(--font-family)', fontSize: 16, fontWeight: 600, color: "#e6e5e8"}}>All clear, captain</p>
                <p style={{position: "absolute", left: 140, top: 310, fontFamily: 'var(--font-family)', fontSize: 14, fontWeight: 400, color: '#9AA4B2'}}>No active alerts at the moment.</p>
                <div style={{position: "absolute", width: 48, height: 48, left: 217, top: 217, border: '1px solid #364152', borderRadius: 'var(--radius-l)'}}></div>
                <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg" style={{position: "absolute", left: 230, top: 230}}>
                  <path d="M20 6L9 17L4 12" stroke="#CDD5DF" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                {mediumDevice ? <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                  <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                </svg> : 
                <svg width="480" height="480" viewBox="0 0 480 480" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="240" cy="240" r="47.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="79.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="111.5" stroke="#202939"/>
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.5} />
                  <circle cx="240" cy="240" r="143.5" stroke="#202939" opacity={0.4} />
                  <circle cx="240" cy="240" r="175.5" stroke="#202939" opacity={0.3} />
                  <circle cx="240" cy="240" r="207.5" stroke="#202939" opacity={0.2} />
                  <circle cx="240" cy="240" r="239.5" stroke="#202939" opacity={0.1} />
                </svg>}
              </div>
              {
                !rows.length && <div style={{position: "relative", top: "-80px"}}>
                  <Button
                    className={classes.alertsBtn}
                    component={RouterLink}
                    variant='outlined'
                    to='/app/alerts/manage'
                    startIcon={<Settings />}
                  >
                      {t('manageAlerts')}
                  </Button> 
                  <CreateAlertButton coin={coin}/>
                </div>
              }
            </Box>
          }
        </Card>
    );
}

export default AlertsTable;
